// src/frontend/js/modern/shared/module-base.js
import { eventManager } from '../core/event-manager.js';
import { eventBus, EVENTS } from '../core/event-bus.js';
import { conflictManager } from '../core/conflict-manager.js';
import { waitForInit } from '../core/init-gate.js';

export class BaseModule {
	constructor(name, store, actions, ui, handlers = []) {
		this.name = name;
		this.store = store;
		this.actions = actions;
		this.ui = ui;
		this.handlers = handlers;
		this._initialized = false;

		eventBus.on('ui:applyState', () => {
			if (this.applyState) this.applyState();
		});
	}

	init() {
		if (this._initialized) return;
		if (this.handlers && this.handlers.length > 0) {
			eventManager.registerModuleHandlers(this.name, this.handlers);
		}
		this._initialized = true;
	}

	getStatus() {
		const count = this.store.count ? this.store.count.get() : 0;
		const items = this.store.items ? this.store.items.get() : [];
		return { count, items: items.length };
	}

	async executeWithConflictManagement(scope, actionId, optimisticFn, rollbackFn, serverFn, onSuccess, onError, config = {}) {
		// First mutation of the session waits (bounded) for the initial server
		// sync so optimistic state never seeds from a stale localStorage.
		await waitForInit();

		return conflictManager.execute({
			scope,
			actionId,
			type: config.type || 'add',
			module: this.name,
			productId: config.productId || null,
			listId: config.listId || null,
			optimisticFn,
			rollbackFn,
			serverFn,
			onSuccess,
			onError,
			// Data-only callback: invoked when the server response arrives
			// but a newer opposite operation is already pending. The module
			// uses this to persist identity data (item_id, wishlist_id)
			// without triggering any UI side-effects.
			onDataOnly: config.onDataOnly || null,
			serverData: config.serverData || null,
			options: config.options || {},
			element: config.element || null
		});
	}

	/**
	 * Capture the current store item + cached display data before an optimistic
	 * removal, so a rollback can restore the exact previous state (no ghost rows).
	 */
	snapshotItem(productId) {
		const item = this.store.getItem ? this.store.getItem(productId) : null;
		const display = this.store.getDisplayData ? this.store.getDisplayData(productId) : null;
		return { item, display };
	}

	/**
	 * Restore a snapshot taken with snapshotItem(). Re-adds the store item with
	 * its original data and patches the UI back (pending state never persists).
	 */
	restoreSnapshot(snapshot, productId) {
		if (!snapshot || !snapshot.item) return;
		if (!this.store.hasItem(productId)) {
			const { _optimistic, _timestamp, ...itemData } = snapshot.item;
			this.store.addItem(productId, itemData);
		}
		if (snapshot.display && this.store.setDisplayData) {
			this.store.setDisplayData(productId, snapshot.display);
		}
		if (this.ui?.confirmCounterItem) {
			this.ui.confirmCounterItem(productId, { ...(snapshot.item || {}), ...(snapshot.display || {}) });
		}
		if (this.ui?.updateAddButton) {
			this.ui.updateAddButton(productId, snapshot.item || {});
		}
	}

	registerHandlers(handlers) {
		if (handlers && handlers.length) {
			eventManager.registerModuleHandlers(this.name, handlers);
		}
	}

	applyState() {}

	reInit() {
		if (this.ui && typeof this.ui.reInit === 'function') {
			this.ui.reInit();
		}
		eventBus.emit(EVENTS.UI_REINIT, { module: this.name });
	}
}
